import {UI, styleRule, StyleSheet, registerStyle} from "../../stem-core/src/ui/UI";
import {Device} from "../../stem-core/src/base/Device";
import {Direction} from "../../stem-core/src/ui/UI";
import {isSmallScreen} from "../UtilsLib";

export class TooltipStyle extends StyleSheet {
    @styleRule
    tooltip = {
        borderRadius: 6,
        display: "none",
        position: "absolute",
        background: this.themeProps.TOOLTIP_BACKGROUND,
        padding: 19,
        color: this.themeProps.TOOLTIP_COLOR,
        fontSize: 16,
        fontWeight: "normal !important",
        zIndex: 9001,
        lineHeight: 20,
        width: () => (Device.isMobileDevice() ? "intrinsic" : ""),
        ">*": {
            whiteSpace: "nowrap",
        },
    };

    render = {
        top: "initial !important",
        left: "initial !important",
        bottom: "initial !important",
        right: "initial !important",
    };

    before = {
        content: '" "',
        display: "block",
        width: 23,
        height: 23,
        position: "absolute",
        borderRadius: ".37rem",
        background: "inherit",
        zIndex: -1,
    };

    @styleRule
    renderAbove = [
        this.render,
        {
            bottom: "",
            transform: "translateX(50%)",
            right: "50%",
            "::before": Object.assign({}, this.before, {
                transform: "translateX(-50%) rotate(-45deg) skew(10deg,10deg)",
                bottom: -9,
                left: "50%",
            }),
        },
    ];

    @styleRule
    renderRight = [
        this.render,
        {
            left: "",
            transform: "translateY(50%)",
            bottom: "50%",
            "::before": Object.assign({}, this.before, {
                transform: "translateY(-50%) skew(10deg,10deg) rotate(45deg)",
                left: -5,
                top: "50%",
            }),
        },
    ];

    @styleRule
    renderBelow = [
        this.render,
        {
            top: "",
            transform: "translateX(50%)",
            right: "50%",
            "::before": Object.assign({}, this.before, {
                transform: "translateX(-50%) rotate(-45deg) skew(10deg,10deg)",
                top: -9,
                left: "50%",
            }),
        },
    ];

    @styleRule
    renderLeft = [
        this.render,
        {
            right: "",
            transform: "translateY(50%)",
            bottom: "50%",
            "::before": Object.assign({}, this.before, {
                transform: "translateY(-50%) skew(10deg,10deg) rotate(45deg)",
                right: -5,
                top: "50%",
            }),
        },
    ];
}

@registerStyle(TooltipStyle)
export class Tooltip extends UI.Element {
    tooltipStyle = {};
    tooltipClass = "";

    constructor(...args) {
        super(...args);

        this.visible = false;
    }

    getDefaultOptions() {
        return Object.assign({}, super.getDefaultOptions(), {
            characterRange: isSmallScreen() ? [12, 24] : [19, 32],
            target: null,
            description: "",
            appearsOnHover: false, // Note: You can't change this option after th e creation of the element.
            parentTarget: false,
            leftRightThreshold: 300,
            upDownThreshold: 200,
            directionHint: null,
            direction: null,
            distance: 15,
        });
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.tooltip);
        attr.addClass(this.tooltipClass);
        attr.setStyle(this.tooltipStyle);
    }

    computeWords(limit) {
        const words = this.options.description.split(" ");
        let message = [];
        let line = "";
        let minLength = 1e4;
        let maxLength = 0;
        for (const word of words) {
            if (line.length + word.length + 1 > limit || line === "") {
                message.push(line === "" ? null : <div>{line}</div>);
                if (line !== "") {
                    minLength = Math.min(minLength, line.length);
                    maxLength = Math.max(maxLength, line.length);
                }
                line = word;
            } else {
                line += " " + word;
            }
        }
        message.push(<div>{line}</div>);
        return {message, delta: maxLength - minLength};
    }

    getOptimalSplit() {
        let bestMessage,
            bestDelta = 1e4;
        const [min, max] = this.options.characterRange;
        for (let limit = max; limit >= min; limit -= 1) {
            const {message, delta} = this.computeWords(limit);
            if (delta < bestDelta) {
                bestMessage = message;
                bestDelta = delta;
            }
        }
        return bestMessage;
    }

    getTarget() {
        return this.options.parentTarget ? this.parent : this.options.target;
    }

    show() {
        this.removeClass(this.styleSheet.renderAbove);
        this.removeClass(this.styleSheet.renderBelow);
        this.removeClass(this.styleSheet.renderLeft);
        this.removeClass(this.styleSheet.renderRight);

        const target = this.getTarget();

        if (target) {
            const {top, bottom, left, right, width, height} = target.node.getBoundingClientRect();

            const {leftRightThreshold, upDownThreshold, directionHint, direction, distance} = this.options;

            const leftDisplay = left + width / 2;
            const rightDisplay = window.innerWidth - right + width / 2;
            const topSpace = top - window.scrollY;
            const bottomSpace = window.innerHeight - bottom;

            if (direction === Direction.LEFT || direction === Direction.RIGHT) {
                this.tooltipStyle = {left: width + distance, right: width + distance};

                if (direction === Direction.LEFT) {
                    this.tooltipClass = this.styleSheet.renderLeft;
                } else if (direction === Direction.RIGHT) {
                    this.tooltipClass = this.styleSheet.renderRight;
                }
            } else if (direction === Direction.UP || direction === Direction.DOWN) {
                this.tooltipStyle = {top: height + distance, bottom: height + distance};

                if (direction === Direction.UP) {
                    this.tooltipClass = this.styleSheet.renderAbove;
                } else if (direction === Direction.DOWN) {
                    this.tooltipClass = this.styleSheet.renderBelow;
                }
            } else if (Math.min(leftDisplay, rightDisplay) * 2 < leftRightThreshold) {
                this.tooltipStyle = {left: width + distance, right: width + distance};

                if (directionHint === Direction.LEFT && leftDisplay > leftRightThreshold) {
                    this.tooltipClass = this.styleSheet.renderLeft;
                } else if (directionHint === Direction.RIGHT && rightDisplay > leftRightThreshold) {
                    this.tooltipClass = this.styleSheet.renderRight;
                } else if (leftDisplay > rightDisplay) {
                    this.tooltipClass = this.styleSheet.renderLeft;
                } else {
                    this.tooltipClass = this.styleSheet.renderRight;
                }
            } else {
                this.tooltipStyle = {top: height + distance, bottom: height + distance};

                if (directionHint === Direction.UP && topSpace > upDownThreshold) {
                    this.tooltipClass = this.styleSheet.renderAbove;
                } else if (directionHint === Direction.DOWN && bottomSpace > upDownThreshold) {
                    this.tooltipClass = this.styleSheet.renderBelow;
                } else if (topSpace > bottomSpace) {
                    this.tooltipClass = this.styleSheet.renderAbove;
                } else {
                    this.tooltipClass = this.styleSheet.renderBelow;
                }
            }
        }
        this.tooltipStyle.display = "block";
        this.visible = true;

        this.redraw();
    }

    hide() {
        this.tooltipStyle = {display: "none"};
        this.tooltipClass = "";
        this.visible = false;

        this.redraw();
    }

    isVisible() {
        return this.visible;
    }

    render() {
        return this.getOptimalSplit();
    }

    onMount() {
        super.onMount();

        if (this.options.appearsOnHover) {
            if (Device.isMobileDevice()) {
                this.getTarget().addNodeListener("touchstart", () => this.show());
                this.getTarget().addNodeListener("touchend", () => setTimeout(() => this.hide(), 1800));
            } else {
                this.getTarget().addNodeListener("mouseenter", () => this.show());
                this.getTarget().addNodeListener("mouseleave", () => this.hide());
            }
        }
    }
}
